import React from 'react';
import type { CompatHandling, CompatReportDocument } from '../types';
import { formatRange, formatTypical } from '../helpers/formatReport';

interface HandlingViewProps {
  result: CompatReportDocument;
}

function resolveHandling(result: CompatReportDocument): CompatHandling | null {
  return result.weldability?.handling || result.handling || null;
}

export const HandlingView: React.FC<HandlingViewProps> = ({ result }) => {
  const h = resolveHandling(result);
  if (!h) {
    return (
      <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 text-[14px] text-heritage-grey">
        No handling block in this report.
      </div>
    );
  }

  const pipesTotal = h.pipesTotal || result.weldability?.pipesTotal || 0;
  const first = Math.round(
    typeof h.fitFirstTime === 'object' ? h.fitFirstTime.typical : Number(h.fitFirstTime) || 0
  );
  const second = Math.round(
    typeof h.neededSecondLook === 'object'
      ? h.neededSecondLook.typical
      : Number(h.neededSecondLook) || 0
  );
  const neverOffered = Math.max(0, pipesTotal - first - second);
  const flippingAllowed = result.settings?.flippingAllowed === true;
  const time = h.time;

  return (
    <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#E6A100]" />
      <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
        Where every pipe in the batch goes, in a typical build
      </p>

      <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-2">
        {[
          {
            v: first,
            k: 'Fit first time',
            s: formatRange(h.fitFirstTime) ? `across builds: ${formatRange(h.fitFirstTime)}` : '',
          },
          {
            v: second,
            k: 'Need a second look',
            s: formatRange(h.neededSecondLook)
              ? `across builds: ${formatRange(h.neededSecondLook)}`
              : '',
          },
          {
            v: neverOffered,
            k: 'Never offered a weld',
            s: neverOffered === 1 ? "the line's first pipe" : "incl. the line's first pipe",
          },
          {
            v: pipesTotal,
            k: 'Pipes in the batch',
            s: 'every scanned pipe',
          },
        ].map((box, i, arr) => (
          <React.Fragment key={box.k}>
            <div className="bg-page-bg border border-[#E2E5EA] rounded-[10px] p-3 text-center flex-1">
              <div className="font-heading font-bold text-[22px] text-blueprint-blue">{box.v}</div>
              <div className="text-[10px] font-semibold tracking-wide uppercase text-heritage-grey mt-1">
                {box.k}
              </div>
              {box.s ? <div className="text-[10px] text-heritage-grey mt-1">{box.s}</div> : null}
            </div>
            {i < arr.length - 1 ? (
              <div className="hidden lg:block font-heading font-bold text-[22px] text-heritage-grey px-0.5">
                {i === arr.length - 2 ? '=' : '+'}
              </div>
            ) : null}
          </React.Fragment>
        ))}
      </div>

      <p className="mt-4 text-[15px] text-text-grey">
        Doing that takes{' '}
        <span className="font-semibold text-blueprint-blue">
          {formatTypical(h.fitChecks?.total)} fit checks
        </span>{' '}
        at the bevel: one first check for each of the{' '}
        {formatTypical(h.fitChecks?.baseline)} pipes offered up, plus{' '}
        {formatTypical(h.fitChecks?.repeats)} more, which are the second and later tries of the{' '}
        {second} pipes that needed another look. Fit checks are work, not pipes, so they do not add
        into the boxes above.
        {flippingAllowed && h.turns ? (
          <>
            {' '}
            It also takes typically{' '}
            <span className="font-semibold text-blueprint-blue">
              {formatTypical(h.turns)} turns
            </span>{' '}
            of a pipe end for end ({formatRange(h.turns)} across builds).
          </>
        ) : null}
      </p>
      <p className="mt-2 text-[12px] text-heritage-grey">
        The big number is a typical build; the small line is the range across builds, which is
        arrival-order luck.
      </p>

      {h.rotation?.statement && (
        <div className="mt-4 border-l-[3px] border-blueprint-blue bg-[rgba(0,39,63,0.05)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
          <span className="font-semibold text-blueprint-blue">{h.rotation.statement}</span>
        </div>
      )}

      {time?.hoursPerBuild ? (
        <div className="mt-3 border-l-[3px] border-[#E6A100] bg-[rgba(230,161,0,0.10)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
          At the supplied rates
          {time.secondsPerFitCheck != null
            ? ` (${time.secondsPerFitCheck}s per fit check`
            : ''}
          {time.secondsPerTurn != null ? `, ${time.secondsPerTurn}s per turn)` : time.secondsPerFitCheck != null ? ')' : ''}
          :{' '}
          <span className="font-semibold text-blueprint-blue">
            {time.hoursPerBuild.typical.toFixed(1)} hours
          </span>{' '}
          per typical build, ranging {time.hoursPerBuild.min.toFixed(1)} to{' '}
          {time.hoursPerBuild.max.toFixed(1)}.
          {time.note ? ` ${time.note}` : ''}
        </div>
      ) : time?.hours != null ? (
        <div className="mt-3 border-l-[3px] border-[#E6A100] bg-[rgba(230,161,0,0.10)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
          Estimated handling time:{' '}
          <span className="font-semibold text-blueprint-blue">{time.hours} hours</span> per build.
        </div>
      ) : (
        <div className="mt-3 border-l-[3px] border-[#E6A100] bg-[rgba(230,161,0,0.10)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
          Handling time is not shown: it is only meaningful if crews physically try pipes, and no
          per-attempt rates were supplied.
        </div>
      )}
    </div>
  );
};
